import {DownloadIcon} from '@heroicons/react/outline';
import html2pdf from 'html2pdf.js';

import {buildDownloadFileName} from '@afriex/core/utils';

import {Button} from '@/components';
import type {Html2PdfOptions} from '@/utils/receipt';
import {removeDarkClasses, removePayazaIfGHS} from '@/utils/receipt';

const GenerateOnePageButton = ({
  fileName,
  scope,
  label,
  dataCy,
}: {
  /** The `<what>` part of the saved name; see `buildDownloadFileName`. */
  fileName: string;
  /** The transaction or person the PDF is for, the `<scope>` part. */
  scope?: string;
  label: string;
  // Caller-supplied: the Details page renders this button twice (visible and
  // in the hidden PDF source), so the two need distinct hooks.
  dataCy?: string;
}) => {
  const generatePDF = async () => {
    const element = document.getElementById('receipt-parent');

    if (!element) {
      console.error("Element with id 'receipt-parent' not found");
      return;
    }

    const clone = element.cloneNode(true) as HTMLElement;
    clone.classList.remove('border-receipt-line', 'border');

    removeDarkClasses(clone);
    removePayazaIfGHS(clone);

    const logoImg = clone.querySelector('#logoPng img') as HTMLImageElement;
    if (logoImg) {
      logoImg.src = '/src/assets/svg/afriex-new-logo.png';
    }

    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.top = '-9999px';
    container.appendChild(clone);
    document.body.appendChild(container);

    const name = buildDownloadFileName(fileName || 'receipt', 'pdf', scope);

    const pdfOptions: Html2PdfOptions = {
      filename: name,
      image: {type: 'png'},
      // useCORS: the KYC receipt's document images are presigned S3 URLs on
      // another origin, which html2canvas leaves out of the canvas unless it
      // requests them in CORS mode (the receipt body then prints blank).
      html2canvas: {scale: 2, useCORS: true},
      jsPDF: {
        unit: 'px',
        format: [clone.offsetWidth, clone.offsetHeight + 5],
        hotfixes: ['px_scaling'],
        compress: true,
      },
    };

    await html2pdf().from(clone).set(pdfOptions).save();

    document.body.removeChild(container);
  };

  return (
    <Button
      onClick={generatePDF}
      colorScheme="cyan"
      variant="solid"
      leftIcon={<DownloadIcon className="h-5 w-5" />}
      data-cy={dataCy}
    >
      {label}
    </Button>
  );
};

export default GenerateOnePageButton;
